import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, publicApi } from "@/lib/api";
import { toast } from "sonner";
import AvatarUpload from "@/components/AvatarUpload";

const AUTH_BG = "https://images.pexels.com/photos/1103970/pexels-photo-1103970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function GoogleButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="google-login-button"
      className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-bold text-[#111827] transition-all hover:border-[#111827]"
    >
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.12A6.97 6.97 0 0 1 5.5 12c0-.74.13-1.45.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </button>
  );
}

export function LoginPage() {
  const { login, startGoogleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const nextUrl = sp.get("next");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || "Login failed");
      return;
    }
    toast.success(`Welcome back, ${res.user.name}`);
    navigate(nextUrl && nextUrl.startsWith("/") ? nextUrl : "/dashboard", { replace: true });
  };

  return <AuthShell title="Sign in" subtitle="Track. Capture. Convert." mode="login">
    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
      <div>
        <label className="label-eyebrow text-[#4B5563]">Email</label>
        <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-base mt-2" placeholder="you@example.com" />
      </div>
      <div>
        <label className="label-eyebrow text-[#4B5563]">Password</label>
        <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-base mt-2" placeholder="••••••••" />
      </div>
      {error && <p className="text-sm text-[#EF4444]" data-testid="login-error">{error}</p>}
      <button type="submit" disabled={submitting} data-testid="login-submit-button" className="btn-primary w-full">
        {submitting ? "Signing in…" : "Sign in"}
      </button>
    </form>

    <div className="my-6 flex items-center gap-3">
      <div className="flex-1 h-px bg-[#E5E7EB]" />
      <span className="label-eyebrow text-[#9CA3AF]">or</span>
      <div className="flex-1 h-px bg-[#E5E7EB]" />
    </div>

    <GoogleButton onClick={startGoogleLogin} />

    <p className="mt-8 text-sm text-[#4B5563]">
      No account?{" "}
      <RouterLink to="/register" data-testid="link-to-register" className="font-bold text-[#111827] hover:text-[#002FA7] underline underline-offset-4">
        Create one
      </RouterLink>
    </p>
  </AuthShell>;
}

export function RegisterPage() {
  const { register, startGoogleLogin } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [picture, setPicture] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [referrer, setReferrer] = useState(null); // { name, picture } if ?ref=CODE is valid
  const refCode = searchParams.get("ref") || "";
  const nextUrl = searchParams.get("next") || "";
  const navigate = useNavigate();

  // Resolve the referral code → referrer's name/picture.
  // Uses the credentials-free publicApi so strict browsers (Safari ITP,
  // Firefox ETP) don't block the cross-origin lookup and incorrectly show
  // "invalid referral" when the call merely failed at the network layer.
  useEffect(() => {
    if (!refCode) return;
    (async () => {
      try {
        const { data } = await publicApi.get(`/referral/info/${encodeURIComponent(refCode)}`);
        setReferrer(data);
      } catch (e) {
        // Only flag the code as invalid for a real 404 from the server.
        // Network / CORS / transient failures stay quiet — the form still
        // works and the affiliate is still credited by the server on
        // /auth/register since we pass refCode through anyway.
        if (e.response?.status === 404) {
          setReferrer({ invalid: true });
        }
      }
    })();
  }, [refCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const res = await register({
      name: name.trim(),
      email: email.trim(),
      password,
      phone: phone.trim() || null,
      address: address.trim() || null,
      picture: picture || null,
      referral_code: refCode || null,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || "Sign up failed");
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    navigate(nextUrl && nextUrl.startsWith("/") ? nextUrl : "/dashboard", { replace: true });
  };

  return <AuthShell title="Create your profile" subtitle="Tell customers who's referring them." mode="register">
    {referrer && !referrer.invalid && (
      <div className="mb-6 rounded-md border border-[#BBF7D0] bg-[#F0FDF4] p-4 flex items-center gap-3" data-testid="register-referrer-banner">
        {referrer.picture ? (
          <img src={referrer.picture} alt={referrer.name} className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="h-10 w-10 rounded-full bg-[#166534] text-white flex items-center justify-center font-bold">{(referrer.name || "U").charAt(0).toUpperCase()}</div>
        )}
        <div>
          <div className="label-eyebrow text-[#166534]">Joining under</div>
          <div className="font-display font-extrabold text-[#111827]">{referrer.name}</div>
        </div>
      </div>
    )}
    {referrer?.invalid && (
      <div className="mb-6 rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3 text-xs text-[#991B1B]" data-testid="register-referrer-invalid">
        The referral code <span className="font-mono">{refCode}</span> doesn't match any affiliate. You can still sign up — you just won't have a referrer.
      </div>
    )}
    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
      <div>
        <label className="label-eyebrow text-[#4B5563] block mb-2">Profile picture</label>
        <AvatarUpload value={picture} onChange={setPicture} name={name} testid="register-avatar" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow text-[#4B5563]">Full name</label>
          <input data-testid="register-name-input" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input-base mt-2" placeholder="Ada Lovelace" />
        </div>
        <div>
          <label className="label-eyebrow text-[#4B5563]">Phone number</label>
          <input data-testid="register-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input-base mt-2" placeholder="+234 801 555 0199" />
        </div>
      </div>
      <div>
        <label className="label-eyebrow text-[#4B5563]">Address</label>
        <input data-testid="register-address-input" type="text" value={address} onChange={(e) => setAddress(e.target.value)} className="input-base mt-2" placeholder="Street, city, country" />
      </div>
      <div>
        <label className="label-eyebrow text-[#4B5563]">Email</label>
        <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-base mt-2" placeholder="you@example.com" />
      </div>
      <div>
        <label className="label-eyebrow text-[#4B5563]">Password</label>
        <input data-testid="register-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="input-base mt-2" placeholder="At least 6 characters" />
      </div>
      <p className="text-xs text-[#9CA3AF]">Your name will appear inside every link you create — e.g. <span className="font-mono">/r/ada-lovelace-x7k2qz</span></p>
      {error && <p className="text-sm text-[#EF4444]" data-testid="register-error">{error}</p>}
      <button type="submit" disabled={submitting} data-testid="register-submit-button" className="btn-primary w-full">
        {submitting ? "Creating account…" : "Create account"}
      </button>
    </form>

    <div className="my-6 flex items-center gap-3">
      <div className="flex-1 h-px bg-[#E5E7EB]" />
      <span className="label-eyebrow text-[#9CA3AF]">or</span>
      <div className="flex-1 h-px bg-[#E5E7EB]" />
    </div>

    <GoogleButton onClick={startGoogleLogin} />

    <p className="mt-8 text-sm text-[#4B5563]">
      Already a member?{" "}
      <RouterLink to="/login" data-testid="link-to-login" className="font-bold text-[#111827] hover:text-[#002FA7] underline underline-offset-4">
        Sign in
      </RouterLink>
    </p>
  </AuthShell>;
}

function AuthShell({ children, title, subtitle, mode }) {
  return (
    <div className="min-h-screen flex bg-white">
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-24 py-12">
        <RouterLink to="/" className="mb-12 flex items-center gap-2 w-fit" data-testid="auth-logo-link">
          <div className="h-9 w-9 rounded-md bg-[#111827] flex items-center justify-center">
            <span className="font-display font-black text-white">T</span>
          </div>
          <div>
            <div className="font-display font-black tracking-tight">TRACE</div>
            <div className="label-eyebrow text-[#4B5563]">Affiliate Tracker</div>
          </div>
        </RouterLink>
        <div className="max-w-sm w-full fade-up">
          <div className="label-eyebrow text-[#002FA7] mb-3" data-testid={`auth-${mode}-eyebrow`}>{mode === "login" ? "Welcome back" : "Get started"}</div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827] mb-2">{title}</h1>
          <p className="text-[#4B5563] mb-10">{subtitle}</p>
          {children}
        </div>
      </div>
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#002FA7]/30 via-transparent to-white/10" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="glass-card rounded-2xl p-6 max-w-sm">
            <div className="label-eyebrow text-[#002FA7]">Why TRACE</div>
            <p className="font-display font-black text-2xl text-[#111827] mt-2 leading-tight">Your name in every link. Every click traced. Every lead captured.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
